import { createSlice, createAsyncThunk } from '@reduxjs/toolkit';
import { documentService } from '../services/documentService';


/**
 * Centralized uploadDocument thunk
 * Accepts minimal payload with component-specific fields
 * Automatically adds common fields from Redux state
 */
export const uploadDocument = createAsyncThunk(
  'documents/uploadDocument',
  async (payload, { rejectWithValue, getState }) => {
    try {
      const state = getState();
      const selectedClaim = state.claims?.selectedClaim;

      // Support new multi-claim/patient arrays
      let claimIds = payload.claimIds || (payload.claimId ? [payload.claimId] : [selectedClaim?.id]);
      let patientIds = payload.patientIds || (payload.patientId ? [payload.patientId] : [selectedClaim?.patientId]);

      const claimId = payload.claimId || selectedClaim?.id;
      const patientId = payload.patientId || selectedClaim?.patientId;

      // If patientIds is an array but empty, include the incoming patientId
      if (Array.isArray(patientIds) && patientIds.length === 0 && patientId) {
        patientIds.push(patientId);
      }
      // If claimIds is an array but empty, include the incoming claimId
      if (Array.isArray(claimIds) && claimIds.length === 0 && claimId) {
        claimIds.push(claimId);
      }

      // if (!claimIds || claimIds.length === 0) {
      //   return rejectWithValue('No claim selected or provided for upload');
      // }

      const {
        file,
        type,
        categories,
        subType,
        name,
        description,
        notes,
        isRebill,
        isAppealDocument = false,
        appealId,
        batchId,
        caseType,
        dateOfService,
        rxNumber,
        assignments,
        reportDefinitionId,
        reportRunId,
        reportFamily,
        reportDefinitionName,
        reportScope,
        generatedFromFilters,
        reportConfigHash,
        providerId,
        providerIds,
        providerName,
        isManual,
        patientName,
        patientFirstName,
        patientLastName,
        uploadToDocumentCenter
      } = payload;

      if (!file) {
        return rejectWithValue('File is required');
      }

      const formData = new FormData();

      // 1. File - include filename for proper multer handling
      const fileName = name || (file instanceof File ? file.name : null) || `DOC_${Date.now()}.pdf`;
      formData.append('file', file, fileName);

      // 2. Multi-claim/patient support
      if (Array.isArray(claimIds) && claimIds.length > 0) {
        formData.append('claimIds', JSON.stringify(claimIds));
      }
      if (Array.isArray(patientIds) && patientIds.length > 0) {
        formData.append('patientIds', JSON.stringify(patientIds));
      }
      if (Array.isArray(categories) && categories.length > 0) {
        formData.append('categories', JSON.stringify(categories));
      }
      if (Array.isArray(assignments) && assignments.length > 0) {
        formData.append('assignments', JSON.stringify(assignments));
      }

      // 3. Patient details from search
      if (patientName) formData.append('patientName', patientName);
      if (patientFirstName) formData.append('patientFirstName', patientFirstName);
      if (patientLastName) formData.append('patientLastName', patientLastName);

      // 4. Document metadata
      formData.append('name', name || fileName);
      formData.append('type', type);
      formData.append('isRebill', isRebill ? 'true' : 'false');
      formData.append('isManual', isManual ? 'true' : 'false');
      if (claimId) formData.append('claimId', claimId);
      formData.append('subType', subType);
      if (description) formData.append('description', description);
      if (notes) formData.append('notes', notes);
      if (rxNumber) formData.append('rxNumber', rxNumber);
      formData.append('isAppealDocument', isAppealDocument);
      if (batchId) {
        formData.append('batchId', batchId);
      }
      if (isAppealDocument) {
        formData.append('appealId', appealId);
      }
      if (caseType) formData.append('caseType', caseType);
      if (dateOfService) formData.append('dateOfService', new Date(dateOfService).toISOString());
      if (reportDefinitionId) formData.append('reportDefinitionId', reportDefinitionId);
      if (reportRunId) formData.append('reportRunId', reportRunId);
      if (reportFamily) formData.append('reportFamily', reportFamily);
      if (reportDefinitionName) formData.append('reportDefinitionName', reportDefinitionName);
      if (reportScope) formData.append('reportScope', JSON.stringify(reportScope));
      if (generatedFromFilters) formData.append('generatedFromFilters', JSON.stringify(generatedFromFilters));
      if (reportConfigHash) formData.append('reportConfigHash', reportConfigHash);
      if (uploadToDocumentCenter !== undefined) {
        formData.append('uploadToDocumentCenter', uploadToDocumentCenter ? 'true' : 'false');
      }

      // Provider fields
      if (providerId) formData.append('providerId', providerId);
      if (Array.isArray(providerIds) && providerIds.length > 0) {
        formData.append('providerIds', JSON.stringify(providerIds));
      }
      if (providerName) formData.append('providerName', providerName);

      const response = await documentService.uploadDocument(formData);
      return response.data || response;

    } catch (error) {
      console.error('Upload failed:', error);
      return rejectWithValue(error.message || 'Upload failed');
    }
  }
);

export const downloadDocument = createAsyncThunk(
  'documents/downloadDocument',
  async (documentId, { rejectWithValue }) => {
    try {
      // We do not call the service here to avoid double-downloading and serializability issues.
      // The component calls documentService.downloadDocument directly for the Blob.
      return { success: true, id: documentId };
    } catch (error) {
      return rejectWithValue(error.message);
    }
  }
);

export const recordDocumentAction = createAsyncThunk(
  'documents/recordDocumentAction',
  async ({ documentId, claimId, action }, { rejectWithValue }) => {
    try {
      await documentService.recordDocumentAction({ documentId, claimId, action });
      return { documentId, action };
    } catch (error) {
      return rejectWithValue(error.message);
    }
  }
);

export const extractEOBData = createAsyncThunk(
  'documents/extractEOBData',
  async ({ documentId, claimId }, { rejectWithValue }) => {
    try {
      const response = await documentService.extractEOBData({ documentId, claimId });
      return response.data || response;
    } catch (error) {
      return rejectWithValue(error.message);
    }
  }
);

export const deleteDocument = createAsyncThunk(
  'documents/deleteDocument',
  async (documentId, { rejectWithValue }) => {
    try {
      await documentService.deleteFile(documentId);
      return documentId;
    } catch (error) {
      return rejectWithValue(error.message);
    }
  }
);

export const updateDocument = createAsyncThunk(
  'documents/updateDocument',
  async ({ documentId, updates }, { rejectWithValue }) => {
    try {
      const response = await documentService.updateDocument(documentId, updates);
      return response.data || response;
    } catch (error) {
      return rejectWithValue(error.message);
    }
  }
);
export const searchDocuments = createAsyncThunk(
  'documents/searchDocuments',
  async (params, { rejectWithValue }) => {
    try {
      const response = await documentService.searchDocuments(params);
      return response.data || response;
    } catch (error) {
      return rejectWithValue(error.message);
    }
  }
);

// Slice
const documentsSlice = createSlice({
  name: 'documents',
  initialState: {
    documents: [],
    total: 0,
    selectedDocument: null,
    searchResults: [],
    searchPagination: null,
    generatedForms: { cms1500: null, rxTransaction: null, paLibc9: null },
    formVerification: { cms1500Verified: false, rxTransactionVerified: false, paLibc9Verified: false },
    formSent: { cms1500Sent: false, rxTransactionSent: false, paLibc9Sent: false },
    loading: false,
    error: null,
  },
  reducers: {
    setSelectedDocument: (state, action) => {
      state.selectedDocument = action.payload;
    },
    clearError: (state) => {
      state.error = null;
    },
    updateFormVerification: (state, action) => {
      const { formType, verified } = action.payload;
      state.formVerification[`${formType}Verified`] = verified;
    },
    updateFormSent: (state, action) => {
      const { formType, sent } = action.payload;
      state.formSent[`${formType}Sent`] = sent;
    },
    setGeneratedForm: (state, action) => {
      const { formType, form } = action.payload;
      state.generatedForms[formType] = form;
    },
    clearSearchResults: (state) => {
      state.searchResults = [];
      state.searchPagination = null;
    },
  },
  extraReducers: (builder) => {
    builder
      // Upload document
      .addCase(uploadDocument.pending, (state) => {
        state.loading = true;
        state.error = null;
      })
      .addCase(uploadDocument.fulfilled, (state, action) => {
        state.loading = false;
        const doc = action.payload.document || action.payload;
        state.documents.push(doc);
        state.selectedDocument = doc;
      })
      .addCase(uploadDocument.rejected, (state, action) => {
        state.loading = false;
        state.error = action.payload;
      })
      // Search documents
      .addCase(searchDocuments.pending, (state) => {
        state.loading = true;
        state.error = null;
      })
      .addCase(searchDocuments.fulfilled, (state, action) => {
        state.loading = false;
        state.searchResults = action.payload.data || action.payload;
        state.documents = action.payload.data || action.payload;
        state.total = action.payload.total || state.searchResults.length;
        state.searchPagination = action.payload.pagination || null;
      })
      .addCase(searchDocuments.rejected, (state, action) => {
        state.loading = false;
        state.error = action.payload;
      })
      // Download document - don't store blob in state
      .addCase(downloadDocument.pending, (state) => {
        state.loading = true;
        state.error = null;
      })
      .addCase(downloadDocument.fulfilled, (state, action) => {
        state.loading = false;
        // Don't store the Blob in state - it will be handled in the component
      })
      .addCase(downloadDocument.rejected, (state, action) => {
        state.loading = false;
        state.error = action.payload;
      })
      // Record document action
      .addCase(recordDocumentAction.pending, (state) => {
        state.loading = true;
        state.error = null;
      })
      .addCase(recordDocumentAction.fulfilled, (state, action) => {
        state.loading = false;
        // No state update needed
      })
      .addCase(recordDocumentAction.rejected, (state, action) => {
        state.loading = false;
        state.error = action.payload;
      })
      // Extract EOB data
      .addCase(extractEOBData.pending, (state) => {
        state.loading = true;
        state.error = null;
      })
      .addCase(extractEOBData.fulfilled, (state, action) => {
        state.loading = false;
        // Store extracted data if needed
      })
      .addCase(extractEOBData.rejected, (state, action) => {
        state.loading = false;
        state.error = action.payload;
      })
      // Delete document
      .addCase(deleteDocument.pending, (state) => {
        state.loading = true;
        state.error = null;
      })
      .addCase(deleteDocument.fulfilled, (state, action) => {
        state.loading = false;
        const documentId = action.payload;
        state.documents = state.documents.filter(doc => doc.id !== documentId);
        // If it was a generated form, set to null
        Object.keys(state.generatedForms).forEach(key => {
          if (state.generatedForms[key]?.id === documentId) {
            state.generatedForms[key] = null;
          }
        });
      })
      .addCase(deleteDocument.rejected, (state, action) => {
        state.loading = false;
        state.error = action.payload;
      })
      // Update document
      .addCase(updateDocument.pending, (state) => {
        state.loading = true;
        state.error = null;
      })
      .addCase(updateDocument.fulfilled, (state, action) => {
        state.loading = false;
        const updatedDoc = action.payload;
        const index = state.documents.findIndex(doc => doc.id === updatedDoc.id);
        if (index !== -1) {
          state.documents[index] = updatedDoc;
        }
        // Update generated forms if applicable
        Object.keys(state.generatedForms).forEach(key => {
          if (state.generatedForms[key]?.id === updatedDoc.id) {
            state.generatedForms[key] = updatedDoc;
          }
        });
      })
      .addCase(updateDocument.rejected, (state, action) => {
        state.loading = false;
        state.error = action.payload;
      });
  },
});

export const { setSelectedDocument, clearError, updateFormVerification, updateFormSent, setGeneratedForm, clearSearchResults } = documentsSlice.actions;
export default documentsSlice.reducer;
